// Spec 45 component-behavior — cmp_ds_textarea.
//
// Variants: default / autosize.
// 6-state matrix (default/hover/focus/disabled/error/at-or-over-max-chars).
//
// Keyboard model:
//   * Tab moves focus OUT (NEVER inserts literal tab).
//   * Enter inserts newline; Mod+Enter submits when allowSubmitShortcut
//     prop is set.
//   * Escape no-op unless wrapped in a dialog (we don't intercept;
//     bubbling is the consumer's job).
//
// Character counter: when maxLength is set, parent renders the counter
// and binds it via aria-describedby. Component itself just propagates
// aria-invalid when value length > maxLength.

import { forwardRef, useCallback, useLayoutEffect, useRef, type KeyboardEvent, type TextareaHTMLAttributes } from "react";

import { cx } from "../lib/cx.ts";

export type TextareaVariant = "default" | "autosize";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly variant?: TextareaVariant;
  readonly error?: boolean;
  /** When true, Mod+Enter (Cmd on Mac, Ctrl elsewhere) fires onSubmitShortcut. */
  readonly allowSubmitShortcut?: boolean;
  readonly onSubmitShortcut?: () => void;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      variant = "default",
      error = false,
      allowSubmitShortcut = false,
      onSubmitShortcut,
      onKeyDown,
      onChange,
      className,
      value,
      defaultValue,
      ...rest
    },
    forwardedRef,
  ) => {
    const localRef = useRef<HTMLTextAreaElement>(null);
    const setRef = useCallback(
      (node: HTMLTextAreaElement | null) => {
        localRef.current = node;
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef !== null) forwardedRef.current = node;
      },
      [forwardedRef],
    );

    // Autosize: recompute height on value change. Reset to 'auto' first
    // so shrinking works; then set to scrollHeight. happy-dom doesn't
    // implement scrollHeight non-trivially in tests — guard by feature
    // detect so the effect is a no-op rather than crashing.
    useLayoutEffect(() => {
      if (variant !== "autosize") return;
      const el = localRef.current;
      if (el === null) return;
      el.style.height = "auto";
      const sh = el.scrollHeight;
      if (sh > 0) el.style.height = `${sh}px`;
    }, [variant, value, defaultValue]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (allowSubmitShortcut && onSubmitShortcut !== undefined) {
          const isMod = event.metaKey || event.ctrlKey;
          if (isMod && event.key === "Enter") {
            event.preventDefault();
            onSubmitShortcut();
          }
        }
        onKeyDown?.(event);
      },
      [allowSubmitShortcut, onSubmitShortcut, onKeyDown],
    );

    return (
      <textarea
        ref={setRef}
        data-component="textarea"
        data-variant={variant}
        data-error={error || undefined}
        aria-invalid={error || undefined}
        className={cx("nai-textarea", className)}
        onKeyDown={handleKeyDown}
        onChange={onChange}
        {...(value !== undefined ? { value } : {})}
        {...(defaultValue !== undefined ? { defaultValue } : {})}
        {...rest}
      />
    );
  },
);

Textarea.displayName = "Textarea";
